/**
 * PlayerAssignmentPreferences implementation manages the mapping between mates and game player ids.
 *
 * Persistence: stored in sessionStorage by room+game for shared-screen convenience.
 */
import { signal } from '@preact/signals-react'
import type {
  MatePlayerAssignmentPreference,
  PlayerAssignmentPreferences,
  PlayerAssignmentPreferencesDeps,
} from './playerAssignmentPreferences.types'

const LEGACY_ASSIGNMENTS_STORAGE_KEY = 'merels_participant_assignments'
const ASSIGNMENTS_STORAGE_KEY = 'merels_player_assignments'

function getStorageKey(base: string, roomName: string, gameId: string | null): string {
  const gameKey = gameId || 'no-game'
  return `${base}_${roomName}_${gameKey}`
}

function loadAssignments(roomName: string, gameId: string | null): MatePlayerAssignmentPreference[] {
  if (typeof window === 'undefined') return []
  try {
    const primary = sessionStorage.getItem(getStorageKey(ASSIGNMENTS_STORAGE_KEY, roomName, gameId))
    if (primary) return JSON.parse(primary)

    // Back-compat: read legacy key if present.
    const legacy = sessionStorage.getItem(getStorageKey(LEGACY_ASSIGNMENTS_STORAGE_KEY, roomName, gameId))
    if (!legacy) return []
    const parsed = JSON.parse(legacy)
    if (!Array.isArray(parsed)) return []

    // Attempt to map legacy shape { participantId, playerId } → { mateId, gamePlayerId }.
    return parsed
      .filter((a: any) => a && typeof a.participantId === 'string' && typeof a.playerId === 'string')
      .map((a: any) => ({ mateId: a.participantId as string, gamePlayerId: a.playerId as string }))
  } catch {
    return []
  }
}

function saveAssignments(roomName: string, gameId: string | null, assignments: MatePlayerAssignmentPreference[]): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.setItem(
      getStorageKey(ASSIGNMENTS_STORAGE_KEY, roomName, gameId),
      JSON.stringify(assignments)
    )
  } catch {
    // Ignore storage errors
  }
}

export function makePlayerAssignmentPreferences(
  _deps: PlayerAssignmentPreferencesDeps = {}
): PlayerAssignmentPreferences {
  const assignmentsSignal = signal<MatePlayerAssignmentPreference[]>([])
  let currentRoomName: string | null = null
  let currentGameId: string | null = null

  function persistIfPossible(): void {
    if (!currentRoomName) return
    saveAssignments(currentRoomName, currentGameId, assignmentsSignal.value)
  }

  function assignMate(mateId: string, gamePlayerId: string): void {
    const filtered = assignmentsSignal.value.filter((a) => a.mateId !== mateId)
    assignmentsSignal.value = [...filtered, { mateId, gamePlayerId }]
    persistIfPossible()
  }

  function unassignMate(mateId: string): void {
    assignmentsSignal.value = assignmentsSignal.value.filter((a) => a.mateId !== mateId)
    persistIfPossible()
  }

  function clearAssignments(): void {
    assignmentsSignal.value = []
    persistIfPossible()
  }

  function loadAssignmentsForRoom(roomName: string, gameId: string | null): void {
    currentRoomName = roomName
    currentGameId = gameId
    assignmentsSignal.value = loadAssignments(roomName, gameId)
  }

  function saveAssignmentsForRoom(roomName: string, gameId: string | null): void {
    saveAssignments(roomName, gameId, assignmentsSignal.value)
  }

  return {
    assignments: assignmentsSignal,
    assignMate,
    unassignMate,
    clearAssignments,
    loadAssignments: loadAssignmentsForRoom,
    saveAssignments: saveAssignmentsForRoom,
  }
}


